/**
 * Inline Issue Actions
 *
 * Action buttons (Acknowledge, Resolve, Dismiss) shown inside expanded timeline cards
 * for container issues that require attention. Each action expands a note textarea + confirm/cancel.
 * After mutation succeeds, shows inline success state for 3 seconds before fading out.
 */

import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { useUpdateIssueStatus } from '@/hooks/api/use-container-issues';
import { toast } from '@/lib/toast-service';

interface InlineIssueActionsProps {
    issueId: string;
    containerId: string;
    currentStatus: string;
    onActionComplete?: () => void;
}

type ActionType = 'acknowledge' | 'resolve' | 'dismiss';

const ACTION_CONFIG: Record<ActionType, {
    label: string;
    targetStatus: string;
    buttonColor: 'secondary' | 'primary' | 'tertiary';
    successMessage: string;
    successLabel: string;
}> = {
    acknowledge: {
        label: 'Acknowledge',
        targetStatus: 'acknowledged',
        buttonColor: 'secondary',
        successMessage: 'Issue acknowledged',
        successLabel: 'Acknowledged by you',
    },
    resolve: {
        label: 'Resolve',
        targetStatus: 'resolved',
        buttonColor: 'primary',
        successMessage: 'Issue resolved',
        successLabel: 'Resolved by you',
    },
    dismiss: {
        label: 'Dismiss',
        targetStatus: 'wont_fix',
        buttonColor: 'tertiary',
        successMessage: 'Issue dismissed',
        successLabel: 'Dismissed by you',
    },
};

function getVisibleActions(currentStatus: string): ActionType[] {
    const actions: ActionType[] = [];
    if (currentStatus === 'open') {
        actions.push('acknowledge');
    }
    if (['open', 'acknowledged', 'in_progress'].includes(currentStatus)) {
        actions.push('resolve');
    }
    if (['open', 'acknowledged'].includes(currentStatus)) {
        actions.push('dismiss');
    }
    return actions;
}

export function InlineIssueActions({
    issueId,
    containerId,
    currentStatus,
    onActionComplete,
}: InlineIssueActionsProps) {
    const [activeAction, setActiveAction] = useState<ActionType | null>(null);
    const [completedAction, setCompletedAction] = useState<ActionType | null>(null);
    const [note, setNote] = useState('');
    const updateStatus = useUpdateIssueStatus();

    const visibleActions = getVisibleActions(currentStatus);

    // Auto-clear success state after 3 seconds
    useEffect(() => {
        if (!completedAction) return;
        const timer = setTimeout(() => setCompletedAction(null), 3000);
        return () => clearTimeout(timer);
    }, [completedAction]);

    if (visibleActions.length === 0 && !completedAction) return null;

    const handleConfirm = () => {
        if (!activeAction) return;
        const config = ACTION_CONFIG[activeAction];

        updateStatus.mutate(
            {
                issueId,
                containerId,
                data: {
                    resolution_status: config.targetStatus,
                    ...(note.trim() ? { status_change_reason: note.trim() } : {}),
                },
            },
            {
                onSuccess: () => {
                    toast.success(config.successMessage);
                    setCompletedAction(activeAction);
                    setActiveAction(null);
                    setNote('');
                    onActionComplete?.();
                },
                onError: (error) => {
                    toast.error(error);
                },
            }
        );
    };

    const handleCancel = () => {
        setActiveAction(null);
        setNote('');
    };

    return (
        <div className="mt-3 border-t border-secondary pt-3">
            <AnimatePresence mode="wait">
                {completedAction != null ? (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center gap-2 py-1"
                    >
                        <CheckCircle className="h-4 w-4 text-success-600" />
                        <span className="text-sm font-medium text-success-600">
                            {ACTION_CONFIG[completedAction].successLabel}
                        </span>
                    </motion.div>
                ) : activeAction == null ? (
                    <motion.div
                        key="buttons"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex flex-wrap gap-2"
                    >
                        {visibleActions.map((action) => {
                            const config = ACTION_CONFIG[action];
                            return (
                                <Button
                                    key={action}
                                    size="sm"
                                    color={config.buttonColor}
                                    onClick={() => setActiveAction(action)}
                                >
                                    {config.label}
                                </Button>
                            );
                        })}
                    </motion.div>
                ) : (
                    <motion.div
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="space-y-2"
                    >
                        <p className="text-xs font-medium text-secondary">
                            {ACTION_CONFIG[activeAction].label} — add a note (optional)
                        </p>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Reason or notes..."
                            rows={2}
                            className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder:text-placeholder focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="primary"
                                onClick={handleConfirm}
                                isLoading={updateStatus.isPending}
                                isDisabled={updateStatus.isPending}
                            >
                                Confirm
                            </Button>
                            <Button
                                size="sm"
                                color="tertiary"
                                onClick={handleCancel}
                                isDisabled={updateStatus.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
